import React, { useState } from 'react';
import { FavoriteFile } from '../../hooks/useFavoriteFiles';

type FavoriteFilesPanelProps = {
  favoriteFiles: FavoriteFile[];
  activeFile: string | null;
  onOpenFile: (path: string) => void;
  onRemoveFile: (path: string) => void;
  onClearAll: () => void;
};

function getFileIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'ts':
    case 'tsx':
      return '📘';
    case 'js':
    case 'jsx':
      return '📒';
    case 'css':
    case 'scss':
    case 'sass':
      return '🎨';
    case 'html':
      return '🌐';
    case 'json':
      return '📋';
    case 'md':
      return '📝';
    case 'svg':
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return '🖼️';
    default:
      return '📄';
  }
}

export const FavoriteFilesPanel = React.memo(function FavoriteFilesPanel({
  favoriteFiles,
  activeFile,
  onOpenFile,
  onRemoveFile,
  onClearAll,
}: FavoriteFilesPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);

  if (favoriteFiles.length === 0) {
    return null;
  }

  return (
    <div className="favorite-files-panel">
      <div
        className="favorite-files-header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
        <span className="favorite-files-title">Favorites</span>
        <span className="favorite-files-count">{favoriteFiles.length}</span>
        {!isCollapsed && favoriteFiles.length > 0 && (
          <button
            className="favorite-files-clear"
            onClick={(e) => {
              e.stopPropagation();
              onClearAll();
            }}
            title="Clear all favorites"
          >
            ✕
          </button>
        )}
      </div>
      {!isCollapsed && (
        <div className="favorite-files-list">
          {favoriteFiles.map((file) => (
            <div
              key={file.path}
              className={`favorite-file-item ${activeFile === file.path ? 'active' : ''}`}
              onClick={() => onOpenFile(file.path)}
              onMouseEnter={() => setHoveredPath(file.path)}
              onMouseLeave={() => setHoveredPath(null)}
              title={file.path}
            >
              <span className="favorite-file-star">★</span>
              <span className="favorite-file-icon">{getFileIcon(file.name)}</span>
              <span className="favorite-file-name">{file.name}</span>
              {hoveredPath === file.path && (
                <button
                  className="favorite-file-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(file.path);
                  }}
                  title="Remove from favorites"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});
